import type { TFunction } from "i18next";
import {
  formatDateTimeShortLabel,
  formatDateWithWeekdayLabel,
  formatMinutesDuration,
  formatSignedMinutesDuration,
  formatTimeRangeLabel,
} from "web-apps/company/utils/timeAndDate";
import type { deriveTimeReportDetails } from "./timeReportDetailsPanel.mapper";
import type { ReportedTimeCardModel } from "./timeReportDetailsPanel.reportedTimeCard.contract";
import { resolveStampedTimeRangeValue } from "../../models/personView.stampedTime.mapper";
import { resolveSignedMinutesForResolution } from "../../models/personView.actions";

interface BuildReportedTimeCardModelParams {
  t: TFunction;
  locale: string;
  derived: ReturnType<typeof deriveTimeReportDetails>;
  includeFlexTimeDeviations: boolean;
  isEditMode: boolean;
  isLoading: boolean;
  hasError: boolean;
  isActionLoading: boolean;
  editedReportedStart: string;
  editedReportedEnd: string;
  editedBreakStart: string;
  editedBreakEnd: string;
  canRunActions: boolean;
  deviationToleranceMinutes: number;
}

const resolveLoadStatus = (
  isLoading: boolean,
  hasError: boolean,
): "loading" | "error" | "ready" => {
  if (isLoading) {
    return "loading";
  }

  if (hasError) {
    return "error";
  }

  return "ready";
};

/** Maps panel state and derived time report values into the reported-time card view model. */
export const buildReportedTimeCardModel = (
  params: BuildReportedTimeCardModelParams,
): ReportedTimeCardModel => {
  const signedTotalMinutes = resolveSignedMinutesForResolution(
    params.derived.effectiveTotalMinutes,
    params.derived.resolution,
  );
  const normalizedSignedOvertimeMinutes =
    Math.abs(params.derived.effectiveSignedOvertimeMinutes) <=
    Math.max(0, params.deviationToleranceMinutes)
      ? 0
      : params.derived.effectiveSignedOvertimeMinutes;

  return {
    header: {
      sectionTitle: params.t("TimeReports.ReportedTimeSection"),
      sectionMeta: params.t("TimeReports.ReportUpdatedAt", {
        value: formatDateTimeShortLabel(
          params.derived.reportUpdatedAt,
          params.locale,
        ),
      }),
      showEditButton: !params.isEditMode && params.derived.canEdit,
      editLabel: params.t("General.Edit"),
      isEditButtonDisabled: !params.canRunActions || params.isActionLoading,
    },
    state: {
      isEditMode: params.isEditMode,
      loadStatus: resolveLoadStatus(params.isLoading, params.hasError),
    },
    labels: {
      fromLabel: params.t("General.From"),
      toLabel: params.t("General.To"),
      oopsLabel: params.t("General.Oops"),
      loadErrorLabel: params.t("TimeReports.LoadError"),
    },
    rows: {
      date: {
        label: params.t("CompanyTimeReport.Date"),
        value: formatDateWithWeekdayLabel(
          params.derived.dateSource,
          params.locale,
        ),
      },
      reported: {
        label: params.t("TimeReports.Reported"),
        readValue: formatTimeRangeLabel(
          params.derived.reportedStart,
          params.derived.reportedEnd,
          params.locale,
        ),
        startValue: params.editedReportedStart,
        endValue: params.editedReportedEnd,
      },
      stamped: {
        label: params.t("TimeReports.Stamped"),
        value:
          resolveStampedTimeRangeValue(
            params.derived.stampedStart,
            params.derived.stampedEnd,
            params.locale,
          ) ?? "-",
      },
      planned: {
        label: params.t("TimeReports.Planned"),
        value: formatTimeRangeLabel(
          params.derived.plannedStart,
          params.derived.plannedEnd,
          params.locale,
        ),
      },
      totalTime: {
        label: params.t("CompanyTimeReport.TotalTime"),
        totalValue:
          signedTotalMinutes < 0
            ? formatSignedMinutesDuration(signedTotalMinutes)
            : formatMinutesDuration(signedTotalMinutes),
        overtimeValue:
          params.includeFlexTimeDeviations &&
          normalizedSignedOvertimeMinutes !== 0
            ? formatSignedMinutesDuration(normalizedSignedOvertimeMinutes)
            : null,
      },
      breakTime: {
        label: params.t("CompanyTimeReport.Break"),
        readValue:
          params.derived.breakMinutes > 0
            ? params.t("TimeReports.BreakWithRange", {
                duration: formatMinutesDuration(params.derived.breakMinutes),
                range: formatTimeRangeLabel(
                  params.derived.breakStart,
                  params.derived.breakEnd,
                  params.locale,
                ),
              })
            : "0h",
        startValue: params.editedBreakStart,
        endValue: params.editedBreakEnd,
      },
    },
  };
};
